import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { Plus, X, AlertTriangle, CheckCircle, TrendingDown, TrendingUp, Package, Boxes, ChevronDown, ChevronRight, BarChart3, Trash2, Pencil, Search, RefreshCw, History, ArrowDownRight, ArrowUpRight, Factory } from 'lucide-react';
import PriceAnalytics from '../components/PriceAnalytics';
import ProductCodes from './ProductCodes';
import CodeLabel from '../components/CodeLabel';
import ItemCodeLabel from '../components/ItemCodeLabel';
import MaterialLotHistory from '../components/MaterialLotHistory';
import FgBatchesPanel from '../components/inventory/FgBatchesPanel';
import { useProductCodes } from '../context/ProductCodesContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
// iter197 — Plain-number formatter for kg / pack quantities. Was incorrectly
// re-using `fmt()` (INR currency formatter) on FG quantities, which prepended
// a ₹ symbol to weights like "₹433 kg". Quantities don't have a currency.
const fmtQty = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(parseFloat(n) || 0);

function StockBadge({ current, reorder }) {
  if (current === 0) return <span className="text-xs bg-red-900/40 text-red-300 border border-red-700 px-2 py-0.5 rounded-full">Out of Stock</span>;
  if (current <= reorder) return <span className="text-xs bg-yellow-900/40 text-yellow-300 border border-yellow-700 px-2 py-0.5 rounded-full">Low Stock</span>;
  return <span className="text-xs bg-green-900/40 text-green-300 border border-green-700 px-2 py-0.5 rounded-full">In Stock</span>;
}

export default function Inventory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSeePrices = user?.role === 'owner' || user?.role === 'admin' || user?.permissions?.can_see_prices;
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [tab, setTab] = useState('raw');
  const [materials, setMaterials] = useState([]);
  const [lots, setLots] = useState([]);
  // iter238 — server-side lot finder so a CONSUMED lot number still leads to
  // its raw material (the inventory list only holds lots with stock left).
  const [lotHits, setLotHits] = useState([]);
  const [finishedGoods, setFinishedGoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMat, setShowAddMat] = useState(false);
  const [showAdjust, setShowAdjust] = useState(null);
  // Mode for the adjust/inward modal — 'inward' (default when opened via Inward Lot picker, lot block visible)
  // or 'adjust' (correction mode for negative qty / wastage write-off)
  const [adjustMode, setAdjustMode] = useState('inward');
  const [showAddFG, setShowAddFG] = useState(false);
  const [batchDetailModal, setBatchDetailModal] = useState(null);
  const [matForm, setMatForm] = useState({
    name: '', unit: 'kg', reorder_level: '', category: '',
    gst_rate: 5, hsn_code: '',
    preferred_vendor: '', preferred_vendor_id: '',
    initial_lot_no: '', initial_qty: '', initial_rate: '',
    initial_vendor_name: '', initial_vendor_id: '', initial_purchase_date: '', initial_expiry_date: '',
  });
  const [adjustForm, setAdjustForm] = useState({
    quantity: '', reason: '', cost_per_unit: '', vendor_name: '', vendor_id: '',
    lot_no: '', purchase_date: '', expiry_date: '', bill_ref: '',
  });
  const [fgForm, setFgForm] = useState({ name: '', unit: 'kg', pack_size_kg: '', buffer_limit_kg: '', price_per_unit: '', gst_rate: 5, hsn_code: '', product_code: '' });
  // Edit FG state
  const [editFg, setEditFg] = useState(null);
  const [editFgForm, setEditFgForm] = useState({ name: '', unit: 'kg', pack_size_kg: '', buffer_limit_kg: '', price_per_unit: '', default_rate: '', gst_rate: 5, hsn_code: '', product_code: '', category: '', notes: '' });
  // Adjust FG stock state
  const [adjustFg, setAdjustFg] = useState(null);
  const [adjustFgMode, setAdjustFgMode] = useState('inward'); // 'inward' | 'adjust'
  const [adjustFgForm, setAdjustFgForm] = useState({ quantity: '', reason: '', batch_id: '', price_per_unit: '', notes: '' });
  // Stock movement timeline drawer
  const [timelineFg, setTimelineFg] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  // FG stock drift report (current_stock vs batch history / negatives)
  const [drift, setDrift] = useState(null);
  // RM total vs lots mismatch (read-only list — nothing is auto-corrected)
  const [lotSync, setLotSync] = useState(null);
  const loadLotSync = async () => {
    try {
      const res = await axios.get(`${API}/inventory/raw-materials/lot-sync`, { withCredentials: true, bypassCache: true });
      setLotSync(res.data);
    } catch (e) { /* advisory only */ }
  };

  useEffect(() => { if (isAdmin) loadLotSync(); /* eslint-disable-next-line */ }, [isAdmin, materials.length]);

  const loadDrift = async () => {
    try {
      const res = await axios.get(`${API}/fg-batches/report`, { withCredentials: true });
      setDrift(res.data);
    } catch (e) { /* advisory only */ }
  };
  const [expandedFg, setExpandedFg] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const canEditBatches = ['owner', 'admin', 'manager'].includes(user?.role);
  const syncAllBatches = async () => {
    setSyncingAll(true);
    try {
      const r = await axios.post(`${API}/fg-batches/sync`, { all: true }, { withCredentials: true });
      toast.success(`${r.data.fixed} product(s) brought in sync — Inventory total = batches`);
      fetchData(); loadDrift();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncingAll(false); }
  };

  const recountFg = async (fg) => {
    const raw = window.prompt(
      `Physical recount — set "${fg.name}" to its real counted quantity.\n\n` +
      `Current on-hand: ${fg.current_stock} ${fg.unit || 'kg'}\n\n` +
      `Enter the actual counted ${fg.unit || 'kg'}:`,
      String(fg.current_stock ?? ''),
    );
    if (raw === null) return;
    const counted = parseFloat(raw);
    if (isNaN(counted) || counted < 0) { toast.error('Enter a valid non-negative number'); return; }
    try {
      const res = await axios.post(`${API}/inventory/finished-goods/${fg.id}/recount`,
        { counted_qty: counted, note: 'Physical recount from Inventory' }, { withCredentials: true });
      const d = res.data?.delta || 0;
      toast.success(`${fg.name} recounted to ${counted} ${fg.unit || 'kg'} (${d >= 0 ? '+' : ''}${d.toFixed ? d.toFixed(2) : d})`);
      fetchData(); loadDrift();
    } catch (err) { toast.error(err.response?.data?.detail || 'Recount failed'); }
  };

  const openTimeline = async (fg) => {
    setTimelineFg(fg);
    setTimelineData(null);
    setTimelineLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/finished-goods/${fg.id}/timeline`, { withCredentials: true });
      setTimelineData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load timeline'); }
    finally { setTimelineLoading(false); }
  };
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedMat, setExpandedMat] = useState(null);
  const [editingReorder, setEditingReorder] = useState(null);
  const [reorderVal, setReorderVal] = useState('');
  // iter223 — AI reorder suggestions (weekly-avg × lead+7d safety).
  //   { [material_id]: { suggested_reorder_level, avg_weekly_consumption,
  //                      consumption_source, delta_pct } }
  const [reorderSuggestions, setReorderSuggestions] = useState({});
  const { refresh: refreshProductCodes } = useProductCodes();
  const [itemCodesList, setItemCodesList] = useState([]);  // for the RM form picker
  const [selectedItemCode, setSelectedItemCode] = useState('');  // code id currently selected
  // NEW: creditors list (vendors come from `clients` collection where type='Creditor' or 'Both')
  const [creditors, setCreditors] = useState([]);
  // NEW: "Add Stock" search-first picker (replaces always-showing Add Material)
  const [showAddStock, setShowAddStock] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  // NEW: Edit Material modal (admin-only) — modify unit/category/reorder/preferred vendor of an existing RM
  const [editMat, setEditMat] = useState(null); // null | material object
  const [editMatForm, setEditMatForm] = useState({ name: '', unit: 'kg', category: '', reorder_level: '', preferred_vendor: '', preferred_vendor_id: '' });

  // Define fetchData before useAdminDelete to avoid TDZ error
  const fetchData = async () => {
    try {
      const [mRes, lRes, fRes] = await Promise.all([
        axios.get(`${API}/inventory/raw-materials-with-lots`, { withCredentials: true }),
        axios.get(`${API}/inventory/lots`, { withCredentials: true }),
        axios.get(`${API}/inventory/finished-goods`, { withCredentials: true }),
      ]);
      setMaterials(mRes.data); setLots(lRes.data); setFinishedGoods(fRes.data);
      loadDrift();
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(fetchData);

  const saveReorderLevel = async (matId) => {
    try {
      await axios.put(`${API}/inventory/raw-materials/${matId}`, { reorder_level: parseFloat(reorderVal) || 0 }, { withCredentials: true });
      setEditingReorder(null);
      fetchData();
      toast.success('Reorder level updated');
    } catch (e) { toast.error('Failed to update'); }
  };

  // adminDelete now uses useAdminDelete hook via requestDelete (see button onClick handlers)

  useEffect(() => { fetchData(); }, []);

  // iter223 — Pull AI reorder suggestions once per page mount. Cheap
  // deterministic endpoint (no LLM); refreshes on manual "Refresh" click.
  const fetchReorderSuggestions = async () => {
    try {
      const res = await axios.get(`${API}/brains/inventory/reorder-suggestions`, { withCredentials: true });
      const map = {};
      (res.data?.suggestions || []).forEach(s => { map[s.material_id] = s; });
      setReorderSuggestions(map);
    } catch { /* non-fatal — brain endpoint may be behind auth for viewers */ }
  };
  useEffect(() => { fetchReorderSuggestions(); }, []);

  // Fetch item codes for the RM-creation picker (admin-only)
  const fetchItemCodes = async () => {
    try {
      const res = await axios.get(`${API}/product-codes/?kind=item`, { withCredentials: true });
      setItemCodesList(res.data || []);
    } catch { /* silent */ }
  };

  // Fetch creditors (vendor list) once — used as dropdown source in opening-lot, adjust, edit forms
  const fetchCreditors = async () => {
    try {
      const res = await axios.get(`${API}/clients/`, { withCredentials: true });
      setCreditors((res.data || []).filter(c => c.type === 'Creditor' || c.type === 'Both'));
    } catch { /* silent */ }
  };
  useEffect(() => { fetchCreditors(); }, []);

  const openAddMat = async () => {
    // Pre-fetch the next global lot number so the suggested opening-lot No is shown
    let nextLotNo = '';
    try {
      const res = await axios.get(`${API}/inventory/lots/next-no`, { withCredentials: true });
      nextLotNo = res.data?.next_lot_no || '';
    } catch { /* leave empty */ }
    setMatForm({
      name: '', unit: 'kg', reorder_level: '', category: '',
      preferred_vendor: '', preferred_vendor_id: '',
      initial_lot_no: nextLotNo, initial_qty: '', initial_rate: '',
      initial_vendor_name: '', initial_vendor_id: '',
      initial_purchase_date: new Date().toISOString().slice(0, 10),
      initial_expiry_date: '',
    });
    setSelectedItemCode('');
    if (isAdmin) fetchItemCodes();
    setShowAddMat(true);
  };

  const handleAddMat = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      // Duplicate-name guard — case-insensitive (server is also a fallback)
      const dup = materials.find(m => (m.name || '').trim().toLowerCase() === (matForm.name || '').trim().toLowerCase());
      if (dup) {
        toast.error(`"${matForm.name}" already exists. Use Adjust Stock to add more.`);
        setSubmitting(false);
        return;
      }
      const res = await axios.post(`${API}/inventory/raw-materials`, matForm, { withCredentials: true });
      setMaterials(prev => [...prev, res.data]);
      // If admin linked an item code, patch that code's real_name to match the RM name
      // so the ItemCodeLabel map resolves correctly everywhere.
      if (selectedItemCode) {
        const code = itemCodesList.find(c => c.id === selectedItemCode);
        if (code && code.real_name !== matForm.name) {
          try {
            await axios.put(`${API}/product-codes/${selectedItemCode}`,
              { real_name: matForm.name },
              { withCredentials: true });
          } catch { /* non-blocking */ }
        }
        refreshProductCodes();
      }
      setShowAddMat(false);
      setMatForm({ name: '', unit: 'kg', reorder_level: '', category: '',
        preferred_vendor: '', preferred_vendor_id: '',
        initial_lot_no: '', initial_qty: '', initial_rate: '',
        initial_vendor_name: '', initial_vendor_id: '',
        initial_purchase_date: '', initial_expiry_date: '' });
      setSelectedItemCode('');
      // If an opening lot was provided, refresh lots so the row appears under expanded RM
      if (matForm.initial_lot_no && parseFloat(matForm.initial_qty) > 0) {
        try {
          const lotsRes = await axios.get(`${API}/inventory/lots`, { withCredentials: true });
          setLots(lotsRes.data || []);
          toast.success(`Material added with opening lot ${matForm.initial_lot_no}`);
        } catch { toast.success('Material added!'); }
      } else {
        toast.success('Material added!');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const handleAdjust = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const qty = parseFloat(adjustForm.quantity);
      if (adjustMode === 'inward' && qty <= 0) {
        toast.error('Inward Lot requires a positive quantity. Use "Adjust" mode for write-offs.');
        setSubmitting(false);
        return;
      }
      // Lot no required for INWARD (positive qty) — backend auto-generates if blank, but we strongly prefer user's own no
      if (qty > 0 && !adjustForm.lot_no.trim()) {
        toast.error('Lot No required when adding stock — needed for FIFO + traceability');
        setSubmitting(false);
        return;
      }
      if (qty > 0 && !adjustForm.vendor_id) {
        toast.error('Pick a vendor (creditor) — needed for ledger linkage');
        setSubmitting(false);
        return;
      }
      await axios.post(`${API}/inventory/raw-materials/${showAdjust.id}/adjust`, {
        quantity: qty, reason: adjustForm.reason,
        cost_per_unit: parseFloat(adjustForm.cost_per_unit) || 0,
        vendor_name: adjustForm.vendor_name || 'Manual Entry',
        vendor_id: adjustForm.vendor_id || undefined,
        lot_no: adjustForm.lot_no.trim() || undefined,
        purchase_date: adjustForm.purchase_date || undefined,
        expiry_date: adjustForm.expiry_date || undefined,
        bill_ref: adjustForm.bill_ref.trim() || undefined,
      }, { withCredentials: true });
      setShowAdjust(null);
      setAdjustForm({ quantity: '', reason: '', cost_per_unit: '', vendor_name: '', vendor_id: '', lot_no: '', purchase_date: '', expiry_date: '', bill_ref: '' });
      fetchData();
      toast.success(qty > 0 ? `Inward lot ${adjustForm.lot_no} created · ${qty} ${showAdjust.unit} from ${adjustForm.vendor_name}` : 'Stock adjusted!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  // Suggest a globally-sequential lot number. Falls back to a local stub if the API is unreachable.
  const suggestLotNo = async () => {
    try {
      const res = await axios.get(`${API}/inventory/lots/next-no`, { withCredentials: true });
      return res.data?.next_lot_no || 'LOT-0001';
    } catch {
      return 'LOT-0001';
    }
  };
  const todayISO = () => new Date().toISOString().slice(0, 10);

  // Open the Adjust Stock / Inward Lot modal — pre-fills traceability fields
  // mode='inward' (default) shows the lot block from the start (capturing a new lot)
  // mode='adjust' opens in correction-only mode (qty can be negative; lot block hidden)
  const openAdjust = async (m, mode = 'inward') => {
    setAdjustMode(mode);
    setShowAdjust(m);
    // Resolve preferred vendor — preferred_vendor_id is the source of truth, fall back to name match
    const credById = m.preferred_vendor_id ? creditors.find(c => c.id === m.preferred_vendor_id) : null;
    const credByName = !credById && m.preferred_vendor ? creditors.find(c => c.name === m.preferred_vendor) : null;
    const cred = credById || credByName;
    const nextLotNo = await suggestLotNo();
    setAdjustForm({
      quantity: '', reason: '', cost_per_unit: '',
      vendor_name: cred?.name || m.preferred_vendor || '',
      vendor_id: cred?.id || '',
      lot_no: nextLotNo,
      purchase_date: todayISO(),
      expiry_date: '',
      bill_ref: '',
    });
  };

  // Open the Edit Material modal pre-filled from row
  const openEditMat = (m) => {
    setEditMat(m);
    setEditMatForm({
      name: m.name || '',
      unit: m.unit || 'kg',
      category: m.category || '',
      reorder_level: m.reorder_level ?? '',
      preferred_vendor: m.preferred_vendor || '',
      preferred_vendor_id: m.preferred_vendor_id || '',
    });
  };

  const handleSaveEditMat = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await axios.put(`${API}/inventory/raw-materials/${editMat.id}`, {
        name: editMatForm.name,
        unit: editMatForm.unit,
        category: editMatForm.category,
        reorder_level: parseFloat(editMatForm.reorder_level) || 0,
        preferred_vendor: editMatForm.preferred_vendor,
        preferred_vendor_id: editMatForm.preferred_vendor_id,
      }, { withCredentials: true });
      setEditMat(null);
      fetchData();
      toast.success('Material updated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating'); }
    finally { setSubmitting(false); }
  };

  const nonKgFg = finishedGoods.filter(fg => (fg.unit || 'kg').toLowerCase() !== 'kg');
  const [converting, setConverting] = useState(false);

  const convertToKg = async () => {
    if (!window.confirm(`Restate ${nonKgFg.length} product(s) — plus their stock, rates, orders and invoices — in kilograms?\n\nQuantity x rate is preserved, so no bill total, ledger balance or margin changes.`)) return;
    setConverting(true);
    try {
      const res = await axios.post(`${API}/unit-migration/run`, {}, { withCredentials: true });
      toast.success(`${res.data.total_changed} record(s) converted to kg`);
      if (res.data.unresolved_total > 0) toast.warning(`${res.data.unresolved_total} item(s) need a pack size first`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Conversion failed'); }
    finally { setConverting(false); }
  };

  const handleAddFG = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const res = await axios.post(`${API}/inventory/finished-goods`, fgForm, { withCredentials: true });
      toast.success('Finished good added!');
      setFinishedGoods(prev => [...prev, res.data]);
      setShowAddFG(false);
      setFgForm({ name: '', unit: 'kg', pack_size_kg: '', buffer_limit_kg: '', price_per_unit: '', gst_rate: 5, hsn_code: '', product_code: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const openEditFg = (fg) => {
    setEditFg(fg);
    setEditFgForm({
      name: fg.name || '',
      unit: fg.unit || 'kg',
      pack_size_kg: fg.pack_size_kg ?? '',
      buffer_limit_kg: fg.buffer_limit_kg ?? '',
      price_per_unit: fg.price_per_unit ?? '',
      default_rate: fg.default_rate ?? '',
      gst_rate: fg.gst_rate ?? 5,
      hsn_code: fg.hsn_code || '',
      product_code: fg.product_code || '',
      category: fg.category || '',
      notes: fg.notes || '',
    });
  };

  const handleSaveEditFg = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await axios.put(`${API}/inventory/finished-goods/${editFg.id}`, editFgForm, { withCredentials: true });
      setEditFg(null);
      fetchData();
      toast.success('Product updated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating'); }
    finally { setSubmitting(false); }
  };

  // Suggest a batch id for manual FG inward — e.g. MAN-260428-0930
  const suggestFgBatchId = () => {
    const d = new Date();
    const pad = n => String(n).padStart(2, '0');
    return `MAN-${String(d.getFullYear()).slice(2)}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  };

  const openAdjustFg = (fg, mode = 'inward') => {
    setAdjustFg(fg);
    setAdjustFgMode(mode);
    setAdjustFgForm({
      quantity: '',
      reason: '',
      batch_id: suggestFgBatchId(),
      price_per_unit: fg.price_per_unit ? String(fg.price_per_unit) : '',
      notes: '',
    });
  };

  const handleAdjustFg = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const qty = parseFloat(adjustFgForm.quantity);
      if (adjustFgMode === 'inward' && qty <= 0) {
        toast.error('Inward requires a positive quantity');
        setSubmitting(false);
        return;
      }
      if (adjustFgMode === 'inward' && !adjustFgForm.batch_id.trim()) {
        toast.error('Batch ID required for inward (for traceability)');
        setSubmitting(false);
        return;
      }
      await axios.post(`${API}/inventory/finished-goods/${adjustFg.id}/adjust`, {
        quantity: qty,
        reason: adjustFgForm.reason,
        batch_id: adjustFgForm.batch_id.trim() || undefined,
        price_per_unit: parseFloat(adjustFgForm.price_per_unit) || undefined,
        notes: adjustFgForm.notes,
      }, { withCredentials: true });
      setAdjustFg(null);
      fetchData();
      toast.success(qty > 0 ? `Added ${qty} ${adjustFg.unit} · batch ${adjustFgForm.batch_id}` : 'FG stock adjusted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  const filteredMats = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return materials;
    // iter220 — Search ALSO matches lot_no across material.lots[] (was
    // name-only, so typing 'LOT-WHEY-260426' returned no rows even though
    // the lot existed under the matching material).
    // iter238 — the list only carries lots with stock left, so a FULLY
    // CONSUMED lot number matched nothing. `lotHits` comes from the server and
    // covers every lot ever created, so an old lot number still finds its
    // material.
    const hitMatIds = new Set((lotHits || []).map(l => l.material_id));
    return materials.filter(m => {
      if ((m.name || '').toLowerCase().includes(q)) return true;
      if ((m.category || '').toLowerCase().includes(q)) return true;
      if (hitMatIds.has(m.id)) return true;
      const lots = m.lots || [];
      return lots.some(l =>
        (l.lot_no || '').toLowerCase().includes(q)
        || (l.vendor_name || '').toLowerCase().includes(q)
        || (l.bill_ref || '').toLowerCase().includes(q)
      );
    });
  }, [materials, search, lotHits]);
  // iter220 — Auto-expand the material whose lot matched, so the operator
  // sees the matching lot row immediately instead of having to click open.
  const lotSearchMatchMat = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return null;
    const m = materials.find(mat =>
      !(mat.name || '').toLowerCase().includes(q)
      && (mat.lots || []).some(l => (l.lot_no || '').toLowerCase().includes(q))
    );
    if (m) return m.id;
    // iter238 — consumed lot: jump to the material the server found.
    const hit = (lotHits || []).find(l => l.material_id);
    return hit ? hit.material_id : null;
  }, [materials, search, lotHits]);
  useEffect(() => {
    if (lotSearchMatchMat) setExpandedMat(lotSearchMatchMat);
  }, [lotSearchMatchMat]);
  // iter238 — debounced server lookup across ALL lots (active + consumed).
  useEffect(() => {
    const q = (search || '').trim();
    if (q.length < 2) { setLotHits([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/inventory/lots/find`, { params: { q }, withCredentials: true })
        .then(r => setLotHits(r.data?.lots || []))
        .catch(() => setLotHits([]));
    }, 350);
    return () => clearTimeout(t);
  }, [search]);
  const filteredLots = lots.filter(l => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (l.material_name || '').toLowerCase().includes(s)
      || (l.lot_no || '').toLowerCase().includes(s)
      || (l.vendor_name || '').toLowerCase().includes(s);
  });
  const filteredFg = finishedGoods.filter(fg => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (fg.name || '').toLowerCase().includes(s)
      || (fg.hsn_code || '').toLowerCase().includes(s)
      || (fg.batch_ids || []).some(b => (b.batch_id || '').toLowerCase().includes(s));
  });
  // iter223 — Materials with reorder_level == 0 are marked "not tracked"
  // by the operator; they shouldn't inflate the low-stock KPI.
  const lowStockCount = materials.filter(m => m.reorder_level > 0 && m.current_stock <= m.reorder_level).length;
  // RM value sums per-material total_lot_value (server-computed) so card matches what rows show
  const rmValue = materials.reduce((s, m) => s + (m.total_lot_value || 0), 0);
  const fgValue = finishedGoods.reduce((s, fg) => s + (fg.current_stock || 0) * (fg.price_per_unit || fg.default_rate || 0), 0);
  const totalValue = rmValue + fgValue;

  if (loading) return <div className="text-zinc-400 animate-pulse text-sm">Loading inventory...</div>;

  return (
    <div className="space-y-5" data-testid="inventory-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Inventory Management</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{materials.length} materials · {lowStockCount} low stock{canSeePrices ? ` · ${fmt(totalValue)} value` : ''}</p>
        </div>
        <button
          data-testid="inventory-refresh-btn"
          onClick={async () => { await Promise.all([fetchData(), fetchCreditors(), fetchReorderSuggestions()]); toast.success('Inventory refreshed'); }}
          disabled={loading}
          title="Refresh inventory + creditor list"
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs px-3 py-2 rounded-xl disabled:opacity-50 transition-colors">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {[
          { label: 'Total Materials', value: materials.length, icon: Boxes, color: 'text-blue-400', bg: 'bg-blue-500/10' },
          { label: 'Low Stock', value: lowStockCount, icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
          ...(canSeePrices ? [
            { label: 'Raw Material Value', value: fmt(rmValue), icon: Boxes, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
            { label: 'Finished Goods Value', value: fmt(fgValue), icon: Package, color: 'text-teal-400', bg: 'bg-teal-500/10' },
            { label: 'Total Inventory', value: fmt(totalValue), icon: Package, color: 'text-green-400', bg: 'bg-green-500/10' },
          ] : []),
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-3" data-testid={`inv-summary-${label.replace(/\s+/g,'-').toLowerCase()}`}>
            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center shrink-0`}><Icon size={16} className={color} /></div>
            <div className="min-w-0"><div className="text-zinc-400 text-xs truncate">{label}</div><div className="font-heading font-semibold text-zinc-50 text-lg font-mono truncate">{value}</div></div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 w-fit overflow-x-auto max-w-full flex-nowrap">
        {[
          ['raw', 'Raw Materials'],
          ['finished', 'Finished Goods'],
          ...(canSeePrices ? [['prices', 'Price Trends']] : []),
          ...(isAdmin ? [['item_codes', 'Item Codes']] : []),
          ...(isAdmin ? [['product_codes', 'Product Codes']] : []),
        ].map(([key, label]) => (
          <button key={key} data-testid={`inv-tab-${key}`} onClick={() => setTab(key)} className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === key ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>{label}</button>
        ))}
      </div>

      {/* RAW MATERIALS */}
      {tab === 'raw' && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by material name, lot no, vendor…" className="flex-1 min-w-[180px] bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" data-testid="mat-search" />
            <button data-testid="add-stock-btn" onClick={() => { setShowAddStock(true); setPickerSearch(''); }} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl"><Plus size={13} /> Inward Lot</button>
            {isAdmin && <button data-testid="new-material-btn" onClick={openAddMat} className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 text-xs px-3 py-2 rounded-xl"><Plus size={13} /> New Material</button>}
          </div>
          {/* iter238 — lot-number lookup across ALL lots, consumed ones too.
              Typing an old lot no now tells you which material it was, when it
              arrived, when it finished and which batch used it. */}
          {isAdmin && lotSync && lotSync.count > 0 && (
            <div className="mb-3 bg-amber-500/[0.06] border border-amber-500/30 rounded-xl px-4 py-3" data-testid="rm-lot-sync-panel">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-amber-400" />
                <p className="text-amber-200 text-xs font-semibold">Total ≠ lots — {lotSync.count} material{lotSync.count > 1 ? 's' : ''} where the stock total disagrees with the sum of lots</p>
                <span className="text-[10px] text-zinc-500 ml-auto">nothing is auto-corrected · open the material to fix deliberately</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] min-w-[560px]">
                  <thead className="text-[10px] text-zinc-500 uppercase tracking-wider">
                    <tr><th className="text-left py-1 pr-2">Material</th><th className="text-right py-1 px-2">Total</th><th className="text-right py-1 px-2">In lots</th><th className="text-right py-1 px-2">Difference</th><th className="text-right py-1 px-2">Open lots</th><th className="text-right py-1 pl-2">Last lot / movement</th></tr>
                  </thead>
                  <tbody>
                    {lotSync.items.slice(0, 12).map(it => (
                      <tr key={it.id} className="border-t border-zinc-800/60" data-testid={`rm-lot-sync-row-${it.id}`}>
                        <td className="py-1 pr-2 text-zinc-200">{it.name}</td>
                        <td className="py-1 px-2 text-right font-mono text-zinc-100">{it.current_stock} {it.unit}</td>
                        <td className="py-1 px-2 text-right font-mono text-zinc-300">{it.lots_sum}</td>
                        <td className={`py-1 px-2 text-right font-mono font-semibold ${it.diff > 0 ? 'text-amber-300' : 'text-red-300'}`}>{it.diff > 0 ? '+' : ''}{it.diff}</td>
                        <td className="py-1 px-2 text-right font-mono text-zinc-400">{it.open_lots}</td>
                        <td className="py-1 pl-2 text-right text-zinc-500 font-mono">{(it.last_lot_date || '').slice(0, 10) || '—'}{it.last_movement_at ? ` · ${it.last_movement_at.slice(0, 10)}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {lotSync.items.length > 12 && <p className="text-[10px] text-zinc-500 mt-1">+{lotSync.items.length - 12} more</p>}
              </div>
            </div>
          )}
          {lotHits.length > 0 && (
            <div className="mb-3 bg-amber-500/[0.07] border border-amber-500/30 rounded-xl px-3 py-2.5 space-y-1.5"
              data-testid="lot-find-banner">
              <p className="text-[11px] text-amber-200 font-semibold">
                {lotHits.length} lot(s) match “{search.trim()}”
                {lotHits.some(l => l.consumed) && ' — including fully consumed lots'}
              </p>
              {lotHits.slice(0, 6).map(l => (
                <div key={l.lot_id} data-testid={`lot-find-${l.lot_no}`} className="text-[11px] text-zinc-300">
                  <button onClick={() => setExpandedMat(l.material_id)}
                    className="font-mono text-amber-300 hover:text-amber-200 underline">{l.lot_no || '—'}</button>
                  {' → '}<span className="text-zinc-100">{l.material_name || 'material deleted'}</span>
                  <span className="text-zinc-500">
                    {' · '}{l.quantity_original} {l.unit} in on {l.purchase_date || '—'}
                    {l.consumed
                      ? ` · finished ${l.finished_at || ''}`
                      : ` · ${l.quantity_remaining} ${l.unit} left`}
                    {l.vendor_name ? ` · ${l.vendor_name}` : ''}
                  </span>
                  {(l.used_in || []).length > 0 && (
                    <span className="text-zinc-500">
                      {' · used in '}
                      {l.used_in.slice(0, 3).map(u => `${u.ref}${u.qty ? ` (${u.qty})` : ''}`).join(', ')}
                    </span>
                  )}
                </div>
              ))}
              {lotHits.length > 6 && <p className="text-[10px] text-zinc-500">+{lotHits.length - 6} more</p>}
            </div>
          )}

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: '900px' }}>
              <thead><tr className="border-b border-zinc-800">
                {['', 'Material Name', ...(isAdmin ? ['Category'] : []), 'Lots', 'Current Stock', 'Reorder Level', 'Stock Bar', 'Status', ...(canSeePrices ? ['Lot Value'] : []), 'Action'].map(h => (
                  <th key={h} className="text-left text-zinc-500 text-xs font-medium px-3 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filteredMats.map(m => {
                  const pct = m.reorder_level > 0 ? Math.min(100, (m.current_stock / (m.reorder_level * 3)) * 100) : 100;
                  const isExpanded = expandedMat === m.id;
                  const matLots = m.lots || [];
                  return (
                    <React.Fragment key={m.id}>
                      <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer" data-testid={`material-row-${m.id}`} onClick={() => setExpandedMat(isExpanded ? null : m.id)}>
                        <td className="px-3 py-3 w-8">
                          {matLots.length > 0 ? (
                            isExpanded ? <ChevronDown size={14} className="text-amber-400" /> : <ChevronRight size={14} className="text-zinc-500" />
                          ) : <span className="text-zinc-700 text-xs">-</span>}
                        </td>
                        <td className="px-3 py-3 text-zinc-200 text-xs font-medium"><ItemCodeLabel name={m.name} /></td>
                        {isAdmin && <td className="px-3 py-3 text-zinc-500 text-xs">{m.category}</td>}
                        <td className="px-3 py-3">
                          <span className="font-mono text-xs bg-zinc-800 text-zinc-300 border border-zinc-700 px-1.5 py-0.5 rounded-md">{matLots.length}</span>
                        </td>
                        <td className="px-3 py-3 font-mono text-zinc-200 text-xs font-semibold">{m.current_stock} {m.unit}</td>
                        <td className="px-3 py-3 font-mono text-zinc-400 text-xs">
                          {editingReorder === m.id ? (
                            <div className="flex items-center gap-1">
                              <input type="number" min="0" value={reorderVal} onChange={e => setReorderVal(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveReorderLevel(m.id); if (e.key === 'Escape') setEditingReorder(null); }}
                                autoFocus className="w-16 bg-zinc-800 border border-amber-500 rounded px-1.5 py-0.5 text-zinc-50 text-xs font-mono" onClick={e => e.stopPropagation()} />
                              <button onClick={(e) => { e.stopPropagation(); saveReorderLevel(m.id); }} className="text-green-400 text-[10px]">Save</button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-start gap-0.5">
                              <span className="cursor-pointer hover:text-amber-400" onClick={(e) => { e.stopPropagation(); setEditingReorder(m.id); setReorderVal(String(m.reorder_level || 0)); }}
                                title="Click to edit">{m.reorder_level} {m.unit}</span>
                              {/* iter223 — AI-suggested reorder pill (weekly consumption × lead+7d safety) */}
                              {(() => {
                                const s = reorderSuggestions[m.id];
                                if (!s || s.suggested_reorder_level == null) return null;
                                const sug = s.suggested_reorder_level;
                                const cur = m.reorder_level || 0;
                                const delta = cur > 0 ? ((sug - cur) / cur * 100) : 100;
                                // Only show a pill when the AI suggestion meaningfully differs (≥15%)
                                if (cur > 0 && Math.abs(delta) < 15) return null;
                                const isUp = sug > cur;
                                const color = isUp ? 'text-amber-400 border-amber-700/50 bg-amber-900/20' : 'text-emerald-400 border-emerald-700/50 bg-emerald-900/20';
                                const applySuggestion = async (e) => {
                                  e.stopPropagation();
                                  if (!isAdmin) { toast.error('Admin only'); return; }
                                  if (!window.confirm(`Set "${m.name}" reorder level to ${sug} ${m.unit}?\n\nBased on ${s.avg_weekly_consumption} ${m.unit}/week production consumption (last 8 weeks) + ${s.consumption_source === 'batch_history' ? 'batch history' : 'upcoming orders'}.`)) return;
                                  try {
                                    await axios.put(`${API}/inventory/raw-materials/${m.id}`, { reorder_level: sug }, { withCredentials: true });
                                    toast.success(`Reorder level updated to ${sug} ${m.unit}`);
                                    await Promise.all([fetchData(), fetchReorderSuggestions()]);
                                  } catch { toast.error('Failed to update'); }
                                };
                                return (
                                  <button
                                    data-testid={`ai-reorder-suggestion-${m.id}`}
                                    onClick={applySuggestion}
                                    title={`AI suggests ${sug} ${m.unit} based on ${s.avg_weekly_consumption} ${m.unit}/week average production consumption (last 8 weeks). Click to apply.`}
                                    className={`text-[9px] px-1.5 py-0.5 rounded border ${color} flex items-center gap-1 font-semibold hover:brightness-125 transition-all`}>
                                    <span>AI: {sug}</span>
                                    {isUp ? <TrendingUp size={9}/> : <TrendingDown size={9}/>}
                                    <span className="opacity-60">{Math.abs(delta).toFixed(0)}%</span>
                                  </button>
                                );
                              })()}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 w-32">
                          <div className="w-full bg-zinc-800 rounded-full h-2">
                            <div className={`h-2 rounded-full transition-all ${pct > 50 ? 'bg-green-500' : pct > 25 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                        <td className="px-3 py-3"><StockBadge current={m.current_stock} reorder={m.reorder_level} /></td>
                        {canSeePrices && <td className="px-3 py-3 font-mono text-amber-400 text-xs">{fmt(m.total_lot_value || 0)}</td>}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <button data-testid={`adjust-stock-${m.id}`} onClick={(e) => { e.stopPropagation(); openAdjust(m, 'adjust'); }} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 px-2 py-1 rounded-lg transition-colors">Adjust</button>
                            {isAdmin && <button data-testid={`reset-mat-${m.id}`} onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Reset "${m.name}" stock to 0?\n\nCurrent: ${m.current_stock} ${m.unit || 'kg'}\n\n• All lot history is preserved (marked reset_zero)\n• Cannot be undone — you'll need to record inward again`)) return;
                                try {
                                  await axios.post(`${API}/inventory/raw-materials/${m.id}/reset-to-zero`, { notes: 'Manual reset from Inventory' }, { withCredentials: true });
                                  toast.success(`${m.name} reset to 0 · lot history preserved`);
                                  fetchData();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
                              }} title="Reset stock to 0 (keep lot history)" className="p-1 rounded-lg bg-amber-900/30 text-amber-400 hover:text-amber-300 border border-amber-800/30"><AlertTriangle size={12} /></button>}
                            {isAdmin && <button data-testid={`edit-mat-${m.id}`} onClick={(e) => { e.stopPropagation(); openEditMat(m); }} title="Edit material" className="p-1 rounded-lg bg-blue-900/30 text-blue-400 hover:text-blue-300 border border-blue-800/30"><Pencil size={12} /></button>}
                    {isAdmin && <button data-testid={`delete-mat-${m.id}`} onClick={(e) => { e.stopPropagation(); requestDelete(`/inventory/raw-materials/${m.id}`, m.name, `Delete "${m.name}" and all its lots. Stock removed permanently.`); }} className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30"><Trash2 size={12} /></button>}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded — full lot history (active + consumed) with vendor filter */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <MaterialLotHistory
                              materialId={m.id}
                              materialName={m.name}
                              unit={m.unit}
                              isAdmin={isAdmin}
                              canSeePrices={canSeePrices}
                              requestDelete={requestDelete}
                              onChanged={fetchData}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {filteredMats.length === 0 && <div className="text-center text-zinc-500 text-sm py-8">No materials found</div>}
          </div>
        </div>
      )}

      {/* FINISHED GOODS */}
      {tab === 'finished' && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by product name, HSN or batch ID..." className="flex-1 min-w-[180px] bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" data-testid="fg-search" />
            <span className="text-zinc-600 text-xs self-center font-mono">{filteredFg.length}/{finishedGoods.length}</span>
            {isAdmin && <button data-testid="add-fg-btn" onClick={() => setShowAddFG(true)} className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 text-xs px-3 py-2 rounded-xl"><Plus size={13} /> New Product</button>}
          </div>
          {isAdmin && nonKgFg.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3" data-testid="fg-kg-banner">
              <AlertTriangle size={15} className="text-amber-400" />
              <div className="flex-1 min-w-[220px]">
                <p className="text-amber-200 text-xs font-semibold">{nonKgFg.length} product{nonKgFg.length > 1 ? 's' : ''} still measured in {[...new Set(nonKgFg.map(f => f.unit))].join(' / ')}</p>
                <p className="text-zinc-400 text-[11px]">Converting restates stock, rates, orders and invoices in kg. Bill totals never change.</p>
              </div>
              <button data-testid="convert-to-kg-btn" onClick={convertToKg} disabled={converting}
                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-xs font-semibold px-3 py-2 rounded-xl">
                {converting ? 'Converting…' : 'Convert everything to kg'}
              </button>
            </div>
          )}
          {canEditBatches && drift && drift.count > 0 && (
            <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3" data-testid="fg-drift-banner">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={15} className="text-red-400" />
                <p className="text-red-200 text-xs font-semibold flex-1">
                  {drift.count} product{drift.count > 1 ? 's' : ''} out of sync — Inventory total ≠ batches, so Sales vouchers see "no batches"
                </p>
                <button onClick={syncAllBatches} disabled={syncingAll} data-testid="fg-sync-all-btn"
                  className="flex items-center gap-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-xs font-semibold px-3 py-1.5 rounded-lg">
                  <RefreshCw size={12} /> {syncingAll ? 'Syncing…' : `Sync all (${drift.count})`}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {drift.items.slice(0, 8).map(d => (
                  <button key={d.id} onClick={() => { setExpandedFg(d.id); setSearch(''); }} className="text-[11px] font-mono bg-red-900/30 text-red-300 border border-red-800/40 px-2 py-1 rounded-lg hover:bg-red-900/50" data-testid={`fg-drift-${d.id}`}>
                    {d.name}: {d.current_stock} {d.unit} vs batches {d.batch_sum}
                  </button>
                ))}
              </div>
              <p className="text-zinc-400 text-[11px] mt-2">Sync creates one <span className="font-mono text-amber-300">UNBATCHED</span> batch for stock that has no batch (rename / split it later), trims batches that exceed the total{drift.orphan_batches > 0 ? <>, and re-links <b className="text-red-300">{drift.orphan_batches}</b> production batch(es) whose product link is stale (the cause of "Finished good not found")</> : ''}.</p>
            </div>
          )}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead><tr className="border-b border-zinc-800">
                {['', 'Product Name', 'Unit', 'Pack kg', 'Buffer kg', 'Batches', 'Current Stock', 'Batch IDs', ...(canSeePrices ? ['Price/Unit'] : []), 'GST Rate', 'HSN Code', ...(canSeePrices ? ['Stock Value'] : []), ...(isAdmin ? [''] : [])].map((h, hi) => (
                  <th key={`${h}-${hi}`} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filteredFg.map(fg => {
                  const isOpen = expandedFg === fg.id;
                  const nb = (fg.batch_ids || []).length;
                  const bsum = (fg.batch_ids || []).reduce((s, b) => s + (parseFloat(b.quantity ?? b.output_kg) || 0), 0);
                  const off = Math.abs(bsum - (parseFloat(fg.current_stock) || 0)) > 0.005;
                  return (
                  <React.Fragment key={fg.id}>
                  <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer" data-testid={`fg-row-${fg.id}`} onClick={() => setExpandedFg(isOpen ? null : fg.id)}>
                    <td className="px-4 py-3 w-8">{isOpen ? <ChevronDown size={14} className="text-amber-400" /> : <ChevronRight size={14} className="text-zinc-500" />}</td>
                    <td className="px-4 py-3 text-zinc-200 text-xs font-medium"><CodeLabel name={fg.name} /></td>
                    <td className="px-4 py-3 text-xs">
                      <span className={(fg.unit || 'kg').toLowerCase() === 'kg' ? 'text-zinc-400' : 'text-amber-400 font-semibold'}>{fg.unit}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-zinc-500 text-xs" data-testid={`fg-pack-kg-${fg.id}`}>
                      {fg.pack_size_kg ? `${fg.pack_size_kg}${fg.pack_size_source === 'auto' ? ' (auto)' : ''}` : '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" data-testid={`fg-buffer-kg-${fg.id}`}>
                      {fg.buffer_limit_kg
                        ? <span className={fg.below_buffer ? 'text-red-400 font-semibold' : 'text-zinc-500'}>
                            {fg.buffer_limit_kg}{fg.below_buffer ? ' ⚠' : ''}
                          </span>
                        : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-mono text-xs border px-1.5 py-0.5 rounded-md ${off ? 'bg-red-900/30 text-red-300 border-red-800/40' : 'bg-zinc-800 text-zinc-300 border-zinc-700'}`} title={off ? `batches ${bsum} ≠ stock ${fg.current_stock}` : 'batches = stock'} data-testid={`fg-batch-count-${fg.id}`}>{nb}{off ? ' ⚠' : ''}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-zinc-200 text-xs font-semibold">{fg.current_stock} {fg.unit}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 items-center">
                        {(fg.batch_ids || []).length > 0 ? [...fg.batch_ids].sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))).slice(0, 7).map((b, i) => (
                          <button key={i} onClick={(e) => { e.stopPropagation(); setBatchDetailModal({ ...b, fg_id: fg.id, product_name: fg.name, unit: fg.unit }); }}
                            className="text-[9px] font-mono bg-amber-900/30 text-amber-400 border border-amber-700/40 px-1.5 py-0.5 rounded-lg hover:bg-amber-900/50 cursor-pointer"
                            data-testid={`fg-batch-${b.batch_id}`}>
                            {b.batch_id}
                          </button>
                        )) : <span className="text-zinc-600 text-[10px]">—</span>}
                        {(fg.batch_ids || []).length > 7 && <span className="text-zinc-500 text-[9px]">+{fg.batch_ids.length - 7} more</span>}
                        <button onClick={(e) => { e.stopPropagation(); setExpandedFg(isOpen ? null : fg.id); }} data-testid={`fg-batches-open-${fg.id}`} title="Open batch list (edit / rename / split)"
                          className="text-[9px] px-1.5 py-0.5 rounded-lg border border-zinc-700 text-zinc-300 hover:border-amber-500">
                          {canEditBatches ? 'Edit batches' : 'View batches'}
                        </button>
                      </div>
                    </td>
                    {canSeePrices && <td className="px-4 py-3 font-mono text-amber-400 text-xs">{fmt(fg.price_per_unit)}</td>}
                    <td className="px-4 py-3 text-zinc-400 text-xs">{fg.gst_rate}%</td>
                    <td className="px-4 py-3 font-mono text-zinc-500 text-xs">{fg.hsn_code || '-'}</td>
                    {canSeePrices && <td className="px-4 py-3 font-mono text-green-400 text-xs">{fmt(fg.current_stock * fg.price_per_unit)}</td>}
                    {isAdmin && (
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`adjust-fg-${fg.id}`} onClick={() => openAdjustFg(fg, 'adjust')} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 px-2 py-1 rounded-lg transition-colors">Adjust</button>
                          <button data-testid={`recount-fg-${fg.id}`} onClick={() => recountFg(fg)} title="Physical recount — set stock to the real counted quantity" className="text-xs bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-300 border border-emerald-800/40 px-2 py-1 rounded-lg transition-colors">Recount</button>
                          <button data-testid={`reset-fg-${fg.id}`} onClick={async () => {
                              if (!window.confirm(`Reset "${fg.name}" stock to 0?\n\nCurrent: ${fg.current_stock} ${fg.unit || 'kg'}\n\n• Batch history archived to historical_batches\n• Cannot be undone — you'll need to produce/inward again`)) return;
                              try {
                                await axios.post(`${API}/inventory/finished-goods/${fg.id}/reset-to-zero`, { notes: 'Manual reset from Inventory' }, { withCredentials: true });
                                toast.success(`${fg.name} reset to 0 · batch history preserved`);
                                fetchData();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
                            }} title="Reset stock to 0 (archive batch history)" className="p-1 rounded-lg bg-amber-900/30 text-amber-400 hover:text-amber-300 border border-amber-800/30"><AlertTriangle size={12} /></button>
                          <button data-testid={`timeline-fg-${fg.id}`} onClick={() => openTimeline(fg)} title="Stock movement timeline" className="p-1 rounded-lg bg-purple-900/30 text-purple-400 hover:text-purple-300 border border-purple-800/30"><History size={12} /></button>
                          <button data-testid={`edit-fg-${fg.id}`} onClick={() => openEditFg(fg)} title="Edit product" className="p-1 rounded-lg bg-blue-900/30 text-blue-400 hover:text-blue-300 border border-blue-800/30"><Pencil size={12} /></button>
                          <button data-testid={`delete-fg-${fg.id}`} onClick={() => requestDelete(`/inventory/finished-goods/${fg.id}`, fg.name, `Delete finished good "${fg.name}" permanently.`)} className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30"><Trash2 size={12} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={14} className="p-0">
                        <FgBatchesPanel fg={fg} canEdit={canEditBatches} onChanged={() => { fetchData(); }} onOpenBatch={(b) => setBatchDetailModal({ ...b, fg_id: fg.id, product_name: fg.name, unit: fg.unit })} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {finishedGoods.length > 0 && filteredFg.length === 0 && <div className="text-center text-zinc-500 text-sm py-8">No products match your search</div>}
          </div>
        </div>
      )}

      {/* PRICE TRENDS (Admin/Owner only) */}
      {tab === 'prices' && canSeePrices && <PriceAnalytics />}

      {tab === 'item_codes' && isAdmin && <ProductCodes kind="item" />}
      {tab === 'product_codes' && isAdmin && <ProductCodes kind="product" />}


      {/* Add Material Modal */}
      {showAddMat && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg my-4 max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <h3 className="font-heading font-semibold text-zinc-50">Add New Raw Material</h3>
              <button onClick={() => setShowAddMat(false)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleAddMat} className="p-5 space-y-3 overflow-y-auto">
              {isAdmin && (
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block flex items-center justify-between">
                    <span>Link to Item Code (optional)</span>
                    {itemCodesList.length === 0 && <span className="text-[10px] text-zinc-600">No codes registered yet</span>}
                  </label>
                  <select
                    data-testid="link-item-code"
                    value={selectedItemCode}
                    onChange={e => {
                      const codeId = e.target.value;
                      setSelectedItemCode(codeId);
                      if (codeId) {
                        const c = itemCodesList.find(x => x.id === codeId);
                        if (c?.real_name) setMatForm(f => ({ ...f, name: c.real_name }));
                      }
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500 mb-1">
                    <option value="">— No item code —</option>
                    {itemCodesList.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.code}{c.real_name ? ` · ${c.real_name}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-zinc-600">New codes are added in Inventory → Item Codes. Picking a code will pre-fill the name below.</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Material Name *</label>
                  <input required type="text" value={matForm.name} onChange={e => setMatForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  {matForm.name && materials.some(m => (m.name || '').trim().toLowerCase() === matForm.name.trim().toLowerCase()) && (
                    <p className="text-[10px] text-red-400 mt-1">⚠ "{matForm.name}" already exists. Use Add Stock instead.</p>
                  )}
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Reorder Level *</label>
                  <input required type="number" value={matForm.reorder_level} onChange={e => setMatForm(f => ({ ...f, reorder_level: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div><label className="text-zinc-400 text-xs mb-1 block">Unit</label>
                  <select value={matForm.unit} onChange={e => setMatForm(f => ({ ...f, unit: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {['kg', 'g', 'litre', 'ml', 'pcs', 'box'].map(u => <option key={u} value={u} className="bg-zinc-900">{u}</option>)}
                  </select>
                </div>
                {isAdmin && (
                  <div className="sm:col-span-2"><label className="text-zinc-400 text-xs mb-1 block">Category (admin only)</label>
                    <input value={matForm.category} onChange={e => setMatForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                )}
                {/* GST slab — used ONLY when this RM is sold directly via a
                    Sales Invoice voucher. Most RMs are consumed in
                    production and never carry GST through this field; this
                    rate kicks in only for direct-resale flows. */}
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">GST % <span className="text-zinc-600 text-[10px]">(sales-invoice only)</span></label>
                  <select value={matForm.gst_rate} onChange={e => setMatForm(f => ({ ...f, gst_rate: parseFloat(e.target.value) }))}
                    data-testid="new-mat-gst-rate"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {[0, 5, 12, 18, 28].map(g => <option key={g} value={g} className="bg-zinc-900">{g}%</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">HSN Code</label>
                  <input value={matForm.hsn_code} placeholder="e.g. 0910" onChange={e => setMatForm(f => ({ ...f, hsn_code: e.target.value }))}
                    data-testid="new-mat-hsn"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block flex items-center justify-between">
                    <span>Preferred Vendor (Creditor)</span>
                    <a href="/creditors" className="text-[10px] text-amber-400 hover:text-amber-300 underline" target="_blank" rel="noopener">+ New</a>
                  </label>
                  <select
                    data-testid="new-mat-pref-vendor"
                    value={matForm.preferred_vendor_id}
                    onChange={e => {
                      const cred = creditors.find(c => c.id === e.target.value);
                      setMatForm(f => ({
                        ...f,
                        preferred_vendor_id: e.target.value,
                        preferred_vendor: cred?.name || '',
                        // Also pre-select on the opening lot (operator can override)
                        initial_vendor_id: e.target.value,
                        initial_vendor_name: cred?.name || '',
                      }));
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    <option value="">— None pinned —</option>
                    {creditors.map(c => <option key={c.id} value={c.id} className="bg-zinc-900">{c.name}{c.city ? ` · ${c.city}` : ''}</option>)}
                  </select>
                  <p className="text-[10px] text-zinc-600 mt-1">Pinning a vendor here means future Inward Lots auto-pick this creditor — no need to choose each time.</p>
                </div>
              </div>
              {/* Optional opening lot — admin can seed initial stock with a lot no for FIFO */}
              {isAdmin && (
                <div className="border-t border-zinc-800 pt-3 mt-1">
                  <p className="text-amber-400 text-[11px] font-semibold uppercase tracking-wider mb-2">Opening Lot (optional)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-zinc-400 text-[10px] mb-1 block">Lot No</label>
                      <input data-testid="form-lot-no" value={matForm.initial_lot_no} onChange={e => setMatForm(f => ({ ...f, initial_lot_no: e.target.value }))} placeholder="e.g. LOT-202604-001" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs font-mono focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-[10px] mb-1 block">Qty ({matForm.unit})</label>
                      <input data-testid="form-lot-qty" type="number" min="0" step="any" value={matForm.initial_qty} onChange={e => setMatForm(f => ({ ...f, initial_qty: e.target.value }))} placeholder="0" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-[10px] mb-1 block">Rate / {matForm.unit}</label>
                      <input data-testid="form-lot-rate" type="number" min="0" step="any" value={matForm.initial_rate} onChange={e => setMatForm(f => ({ ...f, initial_rate: e.target.value }))} placeholder="0" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-[10px] mb-1 block">Vendor (Creditor)</label>
                      <select data-testid="form-lot-vendor"
                        value={matForm.initial_vendor_id}
                        onChange={e => {
                          const cred = creditors.find(c => c.id === e.target.value);
                          setMatForm(f => ({ ...f, initial_vendor_id: e.target.value, initial_vendor_name: cred?.name || '' }));
                        }}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500">
                        <option value="">— Pick a creditor —</option>
                        {creditors.map(c => <option key={c.id} value={c.id} className="bg-zinc-900">{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-zinc-400 text-[10px] mb-1 block">Purchase Date</label>
                      <input data-testid="form-lot-date" type="date" value={matForm.initial_purchase_date} onChange={e => setMatForm(f => ({ ...f, initial_purchase_date: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-[10px] mb-1 block">Expiry (opt)</label>
                      <input data-testid="form-lot-expiry" type="date" value={matForm.initial_expiry_date} onChange={e => setMatForm(f => ({ ...f, initial_expiry_date: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500" />
                    </div>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1.5">Leave the lot fields blank if you'll add stock later via "Add Stock" or a purchase order. FIFO consumes oldest lots first.</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAddMat(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Adding...' : 'Add Material'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjust Stock / Inward Lot Modal */}
      {showAdjust && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg my-4 max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-zinc-50 truncate">
                  {adjustMode === 'inward' ? 'Inward Lot · ' : 'Adjust Stock · '}{showAdjust.name}
                </h3>
                <div className="flex items-center gap-1.5 mt-1">
                  <button type="button" onClick={() => setAdjustMode('inward')} className={`text-[10px] px-2 py-0.5 rounded-full border ${adjustMode === 'inward' ? 'bg-amber-500 text-black border-amber-500 font-bold' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>Inward (+)</button>
                  <button type="button" onClick={() => setAdjustMode('adjust')} className={`text-[10px] px-2 py-0.5 rounded-full border ${adjustMode === 'adjust' ? 'bg-orange-500 text-black border-orange-500 font-bold' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>Adjust / Write-off</button>
                </div>
              </div>
              <button onClick={() => setShowAdjust(null)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleAdjust} className="p-5 space-y-3 overflow-y-auto">
              <p className="text-zinc-400 text-xs">Current: <span className="font-mono text-zinc-200 font-semibold">{showAdjust.current_stock} {showAdjust.unit}</span> · Reorder: <span className="font-mono text-zinc-200">{showAdjust.reorder_level} {showAdjust.unit}</span>{showAdjust.category && <> · <span className="text-zinc-500">{showAdjust.category}</span></>}</p>
              {adjustMode === 'inward' && (
                <div className="bg-blue-900/20 border border-blue-800/40 rounded-xl p-2.5 text-[11px] text-blue-300">
                  <span className="font-semibold">Traceability:</span> this inward lot will appear in <span className="font-mono">Lot History</span>, get FIFO-consumed by <span className="font-mono">Production</span> batches, log into the <span className="font-mono">materials_consumed</span> trail, and link back to the vendor's bill via Bill Ref.
                </div>
              )}
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">
                  {adjustMode === 'inward' ? 'Quantity Received *' : 'Quantity Change (+ or -) *'}
                </label>
                <div className="flex items-center gap-2">
                  {adjustMode === 'inward' && <span className="text-amber-400 font-mono text-sm">+</span>}
                  <input data-testid="adjust-qty-input" required type="number" step="any"
                    min={adjustMode === 'inward' ? '0.01' : undefined}
                    value={adjustForm.quantity}
                    onChange={e => setAdjustForm(f => ({ ...f, quantity: e.target.value }))}
                    placeholder={adjustMode === 'inward' ? `e.g. 100 ${showAdjust.unit}` : 'e.g. -50'}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  <span className="text-zinc-500 text-xs">{showAdjust.unit}</span>
                </div>
              </div>
              {/* Lot block — visible in inward mode (or when adjust qty is positive) */}
              {(adjustMode === 'inward' || parseFloat(adjustForm.quantity) > 0) && (
                <div className="border-t border-zinc-800 pt-3">
                  <p className="text-amber-400 text-[11px] font-semibold uppercase tracking-wider mb-2">New Lot Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div className="sm:col-span-2">
                      <label className="text-zinc-400 text-xs mb-1 block flex items-center justify-between">
                        <span>Lot No *</span>
                        <button type="button" onClick={async () => { const n = await suggestLotNo(); setAdjustForm(f => ({ ...f, lot_no: n })); }} className="text-[10px] text-amber-400 hover:text-amber-300 underline">↻ regen</button>
                      </label>
                      <input data-testid="adjust-lotno-input" required value={adjustForm.lot_no} onChange={e => setAdjustForm(f => ({ ...f, lot_no: e.target.value }))} placeholder="e.g. LOT-WHEY-260426" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs font-mono focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Cost / {showAdjust.unit} (₹)</label>
                      <input type="number" step="any" value={adjustForm.cost_per_unit} onChange={e => setAdjustForm(f => ({ ...f, cost_per_unit: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block flex items-center justify-between">
                        <span>Vendor (Creditor) *</span>
                        <a href="/creditors" className="text-[10px] text-amber-400 hover:text-amber-300 underline" target="_blank" rel="noopener">+ New</a>
                      </label>
                      <select data-testid="adjust-vendor-select"
                        required={adjustMode === 'inward' || parseFloat(adjustForm.quantity) > 0}
                        value={adjustForm.vendor_id}
                        onChange={e => {
                          const cred = creditors.find(c => c.id === e.target.value);
                          setAdjustForm(f => ({ ...f, vendor_id: e.target.value, vendor_name: cred?.name || '' }));
                        }}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                        <option value="">— Pick a creditor —</option>
                        {creditors.map(c => <option key={c.id} value={c.id} className="bg-zinc-900">{c.name}{c.city ? ` · ${c.city}` : ''}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Purchase Date</label>
                      <input type="date" value={adjustForm.purchase_date} onChange={e => setAdjustForm(f => ({ ...f, purchase_date: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Expiry (opt)</label>
                      <input type="date" value={adjustForm.expiry_date} onChange={e => setAdjustForm(f => ({ ...f, expiry_date: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-zinc-400 text-xs mb-1 block">Bill / Invoice Ref (opt)</label>
                      <input data-testid="adjust-bill-ref" value={adjustForm.bill_ref} onChange={e => setAdjustForm(f => ({ ...f, bill_ref: e.target.value }))} placeholder="e.g. BL-2026/0410 — vendor invoice no" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                      <p className="text-[10px] text-zinc-600 mt-1">Links the lot back to the Enterprise / Bill Log entry — cross-checks during audits.</p>
                    </div>
                    {parseFloat(adjustForm.quantity) > 0 && parseFloat(adjustForm.cost_per_unit) > 0 && (
                      <div className="sm:col-span-2 bg-zinc-800/40 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-400 flex items-center justify-between">
                        <span>Total cost</span>
                        <span className="font-mono text-amber-400 font-semibold">{fmt(parseFloat(adjustForm.quantity) * parseFloat(adjustForm.cost_per_unit))}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Reason / Note</label>
                <input value={adjustForm.reason} onChange={e => setAdjustForm(f => ({ ...f, reason: e.target.value }))} placeholder={adjustMode === 'inward' ? 'e.g. Received from vendor' : 'e.g. Wastage / recount correction'} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdjust(null)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button data-testid="adjust-submit-btn" type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Saving...' : (adjustMode === 'inward' ? 'Receive Lot' : 'Apply')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Finished Product Modal */}
      {showAddFG && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg my-4 max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <h3 className="font-heading font-semibold text-zinc-50">Add New Finished Product</h3>
              <button onClick={() => setShowAddFG(false)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleAddFG} className="p-5 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Product Name *</label>
                  <input required type="text" value={fgForm.name} onChange={e => setFgForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Product Code</label>
                  <input type="text" value={fgForm.product_code} onChange={e => setFgForm(f => ({ ...f, product_code: e.target.value.toUpperCase() }))} placeholder="e.g. FCM-A" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm font-mono focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Unit</label>
                  <select value={fgForm.unit} onChange={e => setFgForm(f => ({ ...f, unit: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {['kg', 'g', 'packs', 'pcs', 'box', 'btl'].map(u => <option key={u} value={u} className="bg-zinc-900">{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Pack size (kg per pack)</label>
                  <input data-testid="add-fg-pack-size" type="number" step="any" value={fgForm.pack_size_kg}
                    onChange={e => setFgForm(f => ({ ...f, pack_size_kg: e.target.value }))}
                    placeholder="auto from name — e.g. 0.1 for 100g"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Buffer limit (min kg to always hold)</label>
                  <input data-testid="add-fg-buffer-limit" type="number" step="any" value={fgForm.buffer_limit_kg}
                    onChange={e => setFgForm(f => ({ ...f, buffer_limit_kg: e.target.value }))}
                    placeholder="e.g. 200 — used by manufacturing buffer rules"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Price / Unit (₹) *</label>
                  <input required type="number" step="any" value={fgForm.price_per_unit} onChange={e => setFgForm(f => ({ ...f, price_per_unit: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">GST Rate (%)</label>
                  <select value={fgForm.gst_rate} onChange={e => setFgForm(f => ({ ...f, gst_rate: parseInt(e.target.value) }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {[0, 5, 12, 18, 28].map(r => <option key={r} value={r} className="bg-zinc-900">{r}%</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">HSN Code</label>
                  <input type="text" value={fgForm.hsn_code} onChange={e => setFgForm(f => ({ ...f, hsn_code: e.target.value }))} placeholder="e.g. 0910" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm font-mono focus:outline-none focus:border-amber-500" />
                </div>
              </div>
              <p className="text-[10px] text-zinc-600 pt-1">Stock starts at 0. Use "Adjust" on the row (after saving) to add opening stock or receive inward batches.</p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAddFG(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Adding...' : 'Add Product'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Finished Product Modal (admin) */}
      {editFg && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg my-4 max-h-[92vh] flex flex-col" data-testid="edit-fg-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-zinc-50 truncate">Edit Product · {editFg.name}</h3>
                <p className="text-zinc-500 text-[10px] mt-0.5">Stock & batch_ids untouched — use Adjust for qty changes</p>
              </div>
              <button onClick={() => setEditFg(null)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleSaveEditFg} className="p-5 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Product Name</label>
                  <input value={editFgForm.name} onChange={e => setEditFgForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Product Code</label>
                  <input data-testid="edit-fg-code" value={editFgForm.product_code} onChange={e => setEditFgForm(f => ({ ...f, product_code: e.target.value.toUpperCase() }))} placeholder="e.g. FCM-A" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm font-mono focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Unit</label>
                  <select value={editFgForm.unit} onChange={e => setEditFgForm(f => ({ ...f, unit: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {['kg', 'g', 'packs', 'pcs', 'box', 'btl'].map(u => <option key={u} value={u} className="bg-zinc-900">{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Pack size (kg per pack)</label>
                  <input data-testid="edit-fg-pack-size" type="number" step="any" value={editFgForm.pack_size_kg}
                    onChange={e => setEditFgForm(f => ({ ...f, pack_size_kg: e.target.value }))}
                    placeholder={editFg?.pack_size_kg ? `auto: ${editFg.pack_size_kg}` : 'e.g. 0.1 for a 100g pack'}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Buffer limit (min kg to always hold)</label>
                  <input data-testid="edit-fg-buffer-limit" type="number" step="any" value={editFgForm.buffer_limit_kg}
                    onChange={e => setEditFgForm(f => ({ ...f, buffer_limit_kg: e.target.value }))}
                    placeholder="e.g. 200 — used by manufacturing buffer rules"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Price / Unit (₹)</label>
                  <input type="number" step="any" value={editFgForm.price_per_unit} onChange={e => setEditFgForm(f => ({ ...f, price_per_unit: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Default Rate (fallback)</label>
                  <input type="number" step="any" value={editFgForm.default_rate} onChange={e => setEditFgForm(f => ({ ...f, default_rate: e.target.value }))} placeholder="optional" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">GST Rate (%)</label>
                  <select value={editFgForm.gst_rate} onChange={e => setEditFgForm(f => ({ ...f, gst_rate: parseInt(e.target.value) }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {[0, 5, 12, 18, 28].map(r => <option key={r} value={r} className="bg-zinc-900">{r}%</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">HSN Code</label>
                  <input value={editFgForm.hsn_code} onChange={e => setEditFgForm(f => ({ ...f, hsn_code: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm font-mono focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Category</label>
                  <input value={editFgForm.category} onChange={e => setEditFgForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Powder / Masala / Oil" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Notes</label>
                  <input value={editFgForm.notes} onChange={e => setEditFgForm(f => ({ ...f, notes: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
              </div>
              <p className="text-[10px] text-zinc-500 pt-1">Existing stock & batch_ids are preserved. Changing the name updates every reference in vouchers and production outputs.</p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditFg(null)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button data-testid="edit-fg-submit" type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjust / Inward Finished Product Modal */}
      {adjustFg && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg my-4 max-h-[92vh] flex flex-col" data-testid="adjust-fg-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-zinc-50 truncate">
                  {adjustFgMode === 'inward' ? 'Inward Batch · ' : 'Adjust Stock · '}{adjustFg.name}
                </h3>
                <div className="flex items-center gap-1.5 mt-1">
                  <button type="button" onClick={() => setAdjustFgMode('inward')} className={`text-[10px] px-2 py-0.5 rounded-full border ${adjustFgMode === 'inward' ? 'bg-amber-500 text-black border-amber-500 font-bold' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>Inward (+)</button>
                  <button type="button" onClick={() => setAdjustFgMode('adjust')} className={`text-[10px] px-2 py-0.5 rounded-full border ${adjustFgMode === 'adjust' ? 'bg-orange-500 text-black border-orange-500 font-bold' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>Adjust / Write-off</button>
                </div>
              </div>
              <button onClick={() => setAdjustFg(null)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleAdjustFg} className="p-5 space-y-3 overflow-y-auto">
              <p className="text-zinc-400 text-xs">Current: <span className="font-mono text-zinc-200 font-semibold">{adjustFg.current_stock} {adjustFg.unit}</span> · {adjustFg.batch_ids?.length || 0} batches · Rate <span className="font-mono text-zinc-200">{fmt(adjustFg.price_per_unit)}</span></p>
              {adjustFgMode === 'inward' && (
                <div className="bg-blue-900/20 border border-blue-800/40 rounded-xl p-2.5 text-[11px] text-blue-300">
                  <span className="font-semibold">Traceability:</span> creates a new manual batch entry (visible in the Batch IDs column) and is FIFO-consumed on next sale.
                </div>
              )}
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">
                  {adjustFgMode === 'inward' ? 'Quantity Received *' : 'Quantity Change (+ or -) *'}
                </label>
                <div className="flex items-center gap-2">
                  {adjustFgMode === 'inward' && <span className="text-amber-400 font-mono text-sm">+</span>}
                  <input data-testid="adjust-fg-qty" required type="number" step="any"
                    min={adjustFgMode === 'inward' ? '0.01' : undefined}
                    value={adjustFgForm.quantity}
                    onChange={e => setAdjustFgForm(f => ({ ...f, quantity: e.target.value }))}
                    placeholder={adjustFgMode === 'inward' ? `e.g. 100 ${adjustFg.unit}` : 'e.g. -5'}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  <span className="text-zinc-500 text-xs">{adjustFg.unit}</span>
                </div>
              </div>
              {/* Batch block — only for inward */}
              {adjustFgMode === 'inward' && (
                <div className="border-t border-zinc-800 pt-3">
                  <p className="text-amber-400 text-[11px] font-semibold uppercase tracking-wider mb-2">Batch Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div className="sm:col-span-2">
                      <label className="text-zinc-400 text-xs mb-1 block flex items-center justify-between">
                        <span>Batch ID *</span>
                        <button type="button" onClick={() => setAdjustFgForm(f => ({ ...f, batch_id: suggestFgBatchId() }))} className="text-[10px] text-amber-400 hover:text-amber-300 underline">↻ regen</button>
                      </label>
                      <input data-testid="adjust-fg-batchid" required value={adjustFgForm.batch_id} onChange={e => setAdjustFgForm(f => ({ ...f, batch_id: e.target.value }))} placeholder="e.g. MAN-260428-0930" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs font-mono focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Price / {adjustFg.unit} (₹)</label>
                      <input type="number" step="any" value={adjustFgForm.price_per_unit} onChange={e => setAdjustFgForm(f => ({ ...f, price_per_unit: e.target.value }))} placeholder="Defaults to product rate" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Notes</label>
                      <input value={adjustFgForm.notes} onChange={e => setAdjustFgForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Sub-contract output" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Reason / Note</label>
                <input value={adjustFgForm.reason} onChange={e => setAdjustFgForm(f => ({ ...f, reason: e.target.value }))} placeholder={adjustFgMode === 'inward' ? 'e.g. Job-work return / opening stock' : 'e.g. Breakage / recount correction'} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setAdjustFg(null)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button data-testid="adjust-fg-submit" type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Saving...' : (adjustFgMode === 'inward' ? 'Add Batch' : 'Apply')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Batch Detail Modal */}
      {batchDetailModal && (
        <BatchDetailPopup batch={batchDetailModal} onClose={() => setBatchDetailModal(null)} />
      )}

      {/* Add Stock — search-first picker (replaces always-on Add Material) */}
      {showAddStock && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" onClick={() => setShowAddStock(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md my-4 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="add-stock-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <div>
                <h3 className="font-heading font-semibold text-zinc-50">Inward Lot</h3>
                <p className="text-zinc-500 text-[10px] mt-0.5">Receive new stock against an existing material</p>
              </div>
              <button onClick={() => setShowAddStock(false)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  data-testid="picker-search"
                  autoFocus
                  value={pickerSearch}
                  onChange={e => setPickerSearch(e.target.value)}
                  placeholder="Search material name…"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl pl-9 pr-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
                />
              </div>
              <p className="text-[11px] text-zinc-500">Pick the material you want to add stock to. If it doesn't exist, create it as a new material.</p>
              <div className="space-y-1 max-h-[55vh] overflow-y-auto">
                {(() => {
                  const q = pickerSearch.trim().toLowerCase();
                  const list = q
                    ? materials.filter(m => (m.name || '').toLowerCase().includes(q))
                    : materials;
                  if (list.length === 0) {
                    return (
                      <div className="text-center py-6">
                        <p className="text-zinc-500 text-sm mb-3">No material matches "{pickerSearch}"</p>
                        {isAdmin && (
                          <button
                            data-testid="picker-create-new"
                            onClick={() => {
                              setShowAddStock(false);
                              openAddMat();
                              if (pickerSearch.trim()) {
                                setMatForm(f => ({ ...f, name: pickerSearch.trim() }));
                              }
                            }}
                            className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl">
                            <Plus size={13} /> Create "{pickerSearch || 'new material'}"
                          </button>
                        )}
                      </div>
                    );
                  }
                  return list.slice(0, 50).map(m => (
                    <button
                      key={m.id}
                      data-testid={`picker-row-${m.id}`}
                      onClick={() => {
                        setShowAddStock(false);
                        openAdjust(m, 'inward');
                      }}
                      className="w-full text-left bg-zinc-800/60 hover:bg-zinc-800 border border-zinc-800 hover:border-amber-500/40 rounded-lg px-3 py-2 transition-colors flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-zinc-100 text-sm font-medium truncate"><ItemCodeLabel name={m.name} /></div>
                        <div className="text-zinc-500 text-[10px]">{m.current_stock || 0} {m.unit} on hand · reorder {m.reorder_level} {m.unit}{m.category ? ` · ${m.category}` : ''}</div>
                      </div>
                      <span className="text-amber-400 text-xs flex-shrink-0">Add →</span>
                    </button>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Material Modal (admin) */}
      {editMat && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md my-4 max-h-[92vh] flex flex-col" data-testid="edit-mat-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <h3 className="font-heading font-semibold text-zinc-50 truncate pr-2">Edit: {editMat.name}</h3>
              <button onClick={() => setEditMat(null)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleSaveEditMat} className="p-5 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Material Name</label>
                  <input value={editMatForm.name} onChange={e => setEditMatForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Unit</label>
                  <select value={editMatForm.unit} onChange={e => setEditMatForm(f => ({ ...f, unit: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {['kg', 'g', 'litre', 'ml', 'pcs', 'box'].map(u => <option key={u} value={u} className="bg-zinc-900">{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Reorder Level</label>
                  <input type="number" value={editMatForm.reorder_level} onChange={e => setEditMatForm(f => ({ ...f, reorder_level: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Category</label>
                  <input value={editMatForm.category} onChange={e => setEditMatForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Spice / Acid / Dairy" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block flex items-center justify-between">
                    <span>Preferred Vendor (Creditor)</span>
                    <a href="/creditors" className="text-[10px] text-amber-400 hover:text-amber-300 underline" target="_blank" rel="noopener">+ New</a>
                  </label>
                  <select
                    data-testid="edit-mat-vendor"
                    value={editMatForm.preferred_vendor_id}
                    onChange={e => {
                      const cred = creditors.find(c => c.id === e.target.value);
                      setEditMatForm(f => ({
                        ...f,
                        preferred_vendor_id: e.target.value,
                        preferred_vendor: cred?.name || '',
                      }));
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    <option value="">— None pinned —</option>
                    {creditors.map(c => <option key={c.id} value={c.id} className="bg-zinc-900">{c.name}{c.city ? ` · ${c.city}` : ''}</option>)}
                  </select>
                  <p className="text-[10px] text-zinc-600 mt-1">Pre-fills the vendor on every Inward Lot for this material — no need to pick again each time.</p>
                </div>
              </div>
              <p className="text-[10px] text-zinc-500 pt-1">Existing stock and lot history are preserved. Changing the name updates everywhere it's referenced.</p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditMat(null)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button data-testid="edit-mat-submit" type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared <datalist> for vendor inputs — sourced from Creditors */}
      <datalist id="creditor-list">
        {creditors.map(c => <option key={c.id} value={c.name} />)}
      </datalist>

      {/* FG Stock Movement Timeline Drawer */}
      {timelineFg && (
        <div className="fixed inset-0 bg-black/70 flex justify-end z-50" onClick={() => setTimelineFg(null)}>
          <div className="bg-zinc-900 border-l border-zinc-700 w-full max-w-md h-full overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="fg-timeline-drawer">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-zinc-50 truncate">Stock Timeline · {timelineFg.name}</h3>
                {timelineData && (
                  <p className="text-zinc-500 text-[10px] mt-0.5">
                    {timelineData.summary.event_count} events · in {fmtQty(timelineData.summary.total_in)} {timelineData.fg.unit} · out {fmtQty(timelineData.summary.total_out)} {timelineData.fg.unit} · now <span className="text-amber-400 font-mono">{fmtQty(timelineData.fg.current_stock)}</span>
                  </p>
                )}
              </div>
              <button onClick={() => setTimelineFg(null)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {timelineLoading && <p className="text-zinc-500 text-sm animate-pulse">Loading timeline...</p>}
              {!timelineLoading && timelineData && timelineData.events.length === 0 && (
                <div className="text-center py-12 text-zinc-500 text-sm">
                  <History size={32} className="text-zinc-700 mx-auto mb-2" />
                  No stock-affecting events recorded yet for this product.
                </div>
              )}
              {!timelineLoading && timelineData && timelineData.events.length > 0 && (
                <div className="relative">
                  {/* Vertical timeline line */}
                  <div className="absolute left-4 top-2 bottom-2 w-px bg-zinc-800" />
                  <ol className="space-y-3">
                    <li className="relative pl-10 text-[11px] text-zinc-400 pb-2 border-b border-zinc-800/50" data-testid="timeline-now">
                      <div className="absolute left-2 top-0.5 w-5 h-5 rounded-full bg-amber-500/20 border border-amber-600 flex items-center justify-center"><span className="text-[9px] font-mono text-amber-300">↓</span></div>
                      <span className="font-mono">Now: <span className="text-amber-400 font-semibold">{fmtQty(timelineData.fg.current_stock)} {timelineData.fg.unit}</span> · latest movement first</span>
                    </li>
                    {timelineData.events.map((ev, i) => {
                      const Icon = ev.icon === 'factory' ? Factory : ev.icon === 'send' ? ArrowDownRight : ArrowUpRight;
                      const tone = ev.tone === 'green' ? 'bg-green-500/15 border-green-700 text-green-400' : ev.tone === 'red' ? 'bg-red-500/15 border-red-700 text-red-400' : 'bg-amber-500/15 border-amber-700 text-amber-400';
                      const deltaSign = ev.delta > 0 ? '+' : '';
                      return (
                        <li key={i} className="relative pl-10" data-testid={`timeline-event-${i}`}>
                          <div className={`absolute left-1.5 top-0.5 w-6 h-6 rounded-full border ${tone} flex items-center justify-center`}><Icon size={11} /></div>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-zinc-200 text-xs font-medium truncate">{ev.label}</p>
                              <p className="text-[10px] text-zinc-500 mt-0.5">
                                {ev.ts ? new Date(ev.ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                              </p>
                              {ev.note && <p className="text-[10px] text-zinc-600 italic mt-0.5">{ev.note}</p>}
                              {/* iter197 — Batch traceability per sale dispatch.
                                  Mirrors what enterprise bills already print at
                                  the bottom (Production trace by lot). */}
                              {Array.isArray(ev.batch_breakdown) && ev.batch_breakdown.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1" data-testid={`timeline-batch-breakdown-${i}`}>
                                  {ev.batch_breakdown.map((b, bi) => (
                                    <span key={bi} className="text-[10px] font-mono bg-sky-900/30 text-sky-300 border border-sky-700/40 px-1.5 py-0.5 rounded">
                                      {b.batch_id} · {fmtQty(b.qty)} {timelineData.fg.unit}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className={`font-mono text-xs font-semibold ${ev.delta > 0 ? 'text-green-400' : 'text-red-400'}`}>{deltaSign}{fmtQty(ev.delta)} {timelineData.fg.unit}</span>
                              <p className="text-[10px] text-zinc-600 font-mono mt-0.5">balance: {fmtQty(ev.balance_after)}</p>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                    <li className="relative pl-10 text-[11px] text-zinc-500 pt-2 border-t border-zinc-800/50" data-testid="timeline-start">
                      <div className="absolute left-2 top-2.5 w-5 h-5 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center"><span className="text-[9px] font-mono text-zinc-400">0</span></div>
                      <span className="font-mono">Starting balance: {fmtQty(timelineData.fg.starting_balance)} {timelineData.fg.unit}</span>
                    </li>
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
    </div>
  );
}


function BatchDetailPopup({ batch, onClose }) {
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const [trace, setTrace] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const unit = batch.unit || 'kg';
  const q = (n) => `${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${unit}`;

  React.useEffect(() => {
    const run = async () => {
      try {
        const res = await axios.get(`${API}/fg-batches/${batch.fg_id}/batch/${encodeURIComponent(batch.batch_id)}`, { withCredentials: true });
        setTrace(res.data);
      } catch { setTrace(null); }
      finally { setLoading(false); }
    };
    if (batch.fg_id) run(); else setLoading(false);
  }, [batch.fg_id, batch.batch_id]);

  const b = trace?.batch || batch;
  const live = parseFloat(b.quantity) || 0;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="batch-detail-popup">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div>
            <h3 className="font-heading font-bold text-zinc-50 text-sm">Batch <span className="font-mono text-amber-300">{b.batch_id}</span></h3>
            <p className="text-zinc-500 text-[10px]">{batch.product_name || trace?.product?.name} · {b.date || '—'}</p>
          </div>
          <button onClick={onClose}><X size={16} className="text-zinc-500 hover:text-zinc-200" /></button>
        </div>
        <div className="p-5 space-y-3">
          {loading ? <div className="text-zinc-400 text-sm animate-pulse">Loading...</div> : (
            <>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="bg-zinc-800 rounded-xl p-3"><div className="text-zinc-500 text-[10px]">In stock now</div><div className={`font-mono font-semibold ${live > 0 ? 'text-emerald-300' : 'text-zinc-400'}`} data-testid="batch-detail-live">{q(live)}</div>{trace?.batch?.status && trace.batch.status !== 'live' && <div className="text-[9px] text-zinc-500 capitalize">{trace.batch.status}</div>}</div>
                <div className="bg-zinc-800 rounded-xl p-3"><div className="text-zinc-500 text-[10px]">Sold / dispatched</div><div className="font-mono text-zinc-100 font-semibold" data-testid="batch-detail-sold">{q(trace?.sold_total || 0)}</div></div>
                <div className="bg-zinc-800 rounded-xl p-3"><div className="text-zinc-500 text-[10px]">Produced</div><div className="font-mono text-zinc-100 font-semibold">{trace?.produced != null ? q(trace.produced) : '—'}</div>{trace?.production && <div className="text-[9px] text-zinc-500">{trace.production.production_line_name || ''} {(trace.production.production_date || trace.production.completed_at || '').slice(0, 10)}</div>}</div>
              </div>
              <div>
                <h4 className="text-zinc-400 text-xs font-semibold mb-1.5">Sold to</h4>
                {trace?.sales?.length ? (
                  <div className="bg-zinc-800 rounded-xl overflow-hidden">
                    <table className="w-full text-[10px]">
                      <thead><tr className="border-b border-zinc-700">
                        <th className="px-3 py-2 text-left text-zinc-500">Date</th>
                        <th className="px-3 py-2 text-left text-zinc-500">Invoice</th>
                        <th className="px-3 py-2 text-left text-zinc-500">Customer</th>
                        <th className="px-3 py-2 text-right text-zinc-500">Qty</th>
                      </tr></thead>
                      <tbody>
                        {trace.sales.map((s, i) => (
                          <tr key={i} className="border-b border-zinc-800/50" data-testid={`batch-sale-${i}`}>
                            <td className="px-3 py-1.5 font-mono text-zinc-400">{s.date}</td>
                            <td className="px-3 py-1.5 font-mono text-amber-300">{s.ref}</td>
                            <td className="px-3 py-1.5 text-zinc-200">{s.client || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-100">{q(s.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="text-zinc-500 text-xs">Not sold yet.</div>}
              </div>
              {trace?.moves?.length > 0 && (
                <details>
                  <summary className="text-zinc-500 text-[10px] uppercase cursor-pointer">Movements ({trace.moves.length})</summary>
                  <div className="mt-1 space-y-0.5">
                    {trace.moves.slice(0, 20).map(m => (
                      <div key={m.id} className="text-[10px] text-zinc-400 flex gap-2"><span className="font-mono text-zinc-600 shrink-0">{(m.created_at || '').slice(0, 10)}</span><span className={`font-mono shrink-0 w-14 text-right ${m.delta > 0 ? 'text-emerald-300' : m.delta < 0 ? 'text-red-300' : 'text-zinc-500'}`}>{m.delta > 0 ? '+' : ''}{m.delta}</span><span className="truncate">{(m.source_type || '').replace(/_/g, ' ')}{m.reason ? ` · ${m.reason}` : ''}</span></div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
